import { useDebounce, downloadXLSFile, sendGAEvent } from 'utils/utils';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useMemo, useState, useCallback, ReactNode } from 'react';
import { postToast } from 'features/common/utils/postFlash';

import { HelpCircle } from 'lucide-react';
import { AttendeeActions } from './components/AttendeeActions';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { ResponsesData } from 'features/CustomFieldsResponses/types';

import { CustomFieldsResponsesProps } from 'features/CustomFieldsResponses/types';
import { IdParamsType } from 'utils/types';
import {
	EVENT_COLUMN_KEY,
	PAID_COLUMN_DEFS,
	REGISTRATION_COLUMN_DEFS,
	SYSTEM_FIELD_NAMES,
} from 'features/PunchupPages/CustomFieldsResponse/components/PunchupResponsesTable/constants';
import { PunchupFilterSelect } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupFilterSelect';
import { PunchupResponsesTable } from 'features/PunchupPages/CustomFieldsResponse/components/PunchupResponsesTable/PunchupResponsesTable';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupColumnPicker } from 'features/common/components/Punchup/composites/tables/PunchupColumnPicker';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { getData } from 'services/api';
import { useColumnSelection } from 'features/common/hooks/useColumnSelection';
import { useEventCustomFields } from 'features/PunchupPages/CustomFieldsResponse/hooks/useEventCustomFields';
import { useHostTicketBridge } from 'features/PunchupPages/CustomFieldsResponse/hooks/useHostTicketBridge';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTranslation } from 'react-i18next';

export const PunchupCustomFieldsResponses = ({ isAttendeesList = false }: CustomFieldsResponsesProps) => {
	const { t } = useTranslation();
	useHostTicketBridge();

	const { event } = useGetEvent();
	const { id } = useParams<IdParamsType>();
	// scannerGroupId is set on an event whose attendee list spans linked shows — the same signal
	// the mobile picker reads (TIC-705/732). Only the attendee list spans them; the questions view
	// stays one show.
	const isLinkedEventsGroup = isAttendeesList && !!event?.scannerGroupId;
	const { setSearchText, responsesQueryObject, sortBy, order, handleSort, showLinkedEvents, setShowLinkedEvents } =
		useEventCustomFields(isAttendeesList, isLinkedEventsGroup);

	const eventMode = event?.mode || 'normal';
	const isPaidEvent = eventMode !== 'registration';
	const columnDefs = isPaidEvent ? PAID_COLUMN_DEFS : REGISTRATION_COLUMN_DEFS;
	const { visibleKeys, toggleColumn, allDefs } = useColumnSelection(columnDefs);

	// The Show column rides the grouped view: derived here rather than picker-managed, so it
	// appears with the fan-out and leaves when the list narrows to one show.
	const effectiveVisibleKeys = useMemo(() => {
		if (!isLinkedEventsGroup || !showLinkedEvents) return visibleKeys;
		return new Set([...visibleKeys, EVENT_COLUMN_KEY]);
	}, [visibleKeys, isLinkedEventsGroup, showLinkedEvents]);

	// Discover custom-field question names from loaded data.
	// Custom fields are hidden by default — users opt in via the Custom Fields picker.
	const questions = useMemo<string[]>(() => {
		if (!responsesQueryObject.data) return [];
		const seen = new Set<string>();
		responsesQueryObject.data.pages.flat().forEach((response) => {
			response.responses.forEach((r) => {
				if (!SYSTEM_FIELD_NAMES.includes(r.custom_field.name)) {
					seen.add(r.custom_field.name);
				}
			});
		});
		return [...seen];
	}, [responsesQueryObject.data?.pages]);

	const customFieldDefs = useMemo<ColumnDef[]>(
		() => questions.map((q, i) => ({ key: q, labelKey: q, minWidth: 200, priority: i + 1 })),
		[questions]
	);

	const [visibleCustomKeys, setVisibleCustomKeys] = useState<Set<string>>(new Set());

	const toggleCustomColumn = useCallback((key: string) => {
		setVisibleCustomKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	const renderRowActions = useCallback(
		(data: ResponsesData, transactionTickets: Record<string, number[]>): ReactNode => (
			<AttendeeActions data={data} isPaidEvent={isPaidEvent} transactionTickets={transactionTickets} />
		),
		[isPaidEvent]
	);

	const searchDebounce = useDebounce(setSearchText);

	const [isDownloading, setIsDownloading] = useState(false);
	const handleDownload = async () => {
		setIsDownloading(true);
		try {
			const response = await getData(`/events/${id}/custom-fields/responses/download`, {
				params: {
					all_attendees: isAttendeesList,
					...(isLinkedEventsGroup && showLinkedEvents ? { grouped: true } : {}),
				},
				responseType: 'blob',
				headers: { 'Content-Type': 'blob' },
			});

			let fileName = event?.name ? event.name.replace(/[^a-zA-Z0-9]/g, ' ') : 'Attendees';
			if (event?.startDate) {
				const timezone = event.timeZone || 'UTC';
				const startDateTime = DateTime.fromISO(event.startDate, { zone: timezone });
				const formattedDate = startDateTime.toFormat('MMM dd hh:mm a').replace(/:/g, '.');
				fileName += ` (${formattedDate})`;
			}

			downloadXLSFile(response, fileName);
			sendGAEvent({ category: 'Custom Fields', action: 'Custom Fields Responses download' });
		} catch {
			postToast(
				'error',
				t('customFields-responses-download-error', 'Could not export attendee data. Please try again.')
			);
		} finally {
			setIsDownloading(false);
		}
	};

	const linkedShowsOptions = [
		{ value: 'linked', label: t('customFields-responses-linked-shows-all', 'All linked shows') },
		{ value: 'single', label: t('customFields-responses-linked-shows-single', 'This show only') },
	];
	const linkedShowsValue = showLinkedEvents ? 'linked' : 'single';

	const handleRefresh = () => {
		responsesQueryObject.refetch();
		sendGAEvent({ category: 'Custom Fields', action: 'Responses refresh' });
	};

	return (
		<PunchupTablePage
			pageName={isAttendeesList ? 'Attendee List' : 'Custom Questions'}
			header={
				<PunchupTablePageHeader
					title={isAttendeesList ? 'Attendee List' : 'Custom Questions'}
					description={
						isAttendeesList
							? 'View and export all attendees registered for this show.'
							: 'Review what each guest answered to the questions you set on this show.'
					}
					onDownload={handleDownload}
					downloadLabel={t('customFields-responses-download')}
					isDownloading={isDownloading}
					onRefresh={handleRefresh}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={responsesQueryObject.isFetching}
				/>
			}
			search={
				<PunchupSearch onChange={(e) => searchDebounce(e.target.value)} placeholder={t('search-email-or-name')} />
			}
			controlsActions={
				<>
					{isLinkedEventsGroup && (
						<PunchupFilterSelect
							options={linkedShowsOptions}
							value={linkedShowsValue}
							onChange={(val) => setShowLinkedEvents(val === 'linked')}
							ariaLabel={`${t('customFields-responses-linked-shows-filter', 'Linked shows filter')}: ${
								linkedShowsOptions.find((option) => option.value === linkedShowsValue)?.label
							}`}
						/>
					)}
					<PunchupColumnPicker allDefs={allDefs} visibleKeys={visibleKeys} onToggle={toggleColumn} />
					{customFieldDefs.length > 0 && (
						<PunchupColumnPicker
							allDefs={customFieldDefs}
							visibleKeys={visibleCustomKeys}
							onToggle={toggleCustomColumn}
							label={t('column-picker-custom-fields', 'Custom Questions')}
							dropdownLabel={t('column-picker-custom-fields-label', 'Show custom questions')}
							icon={<HelpCircle size={16} />}
							allowEmpty
						/>
					)}
				</>
			}
		>
			{responsesQueryObject.isError ? (
				<PunchupErrorState
					title={t('responses-load-error', 'Failed to load attendee data')}
					description={t('responses-load-error-description', 'We could not load the attendee data. Please try again.')}
					onRetry={() => responsesQueryObject.refetch()}
				/>
			) : (
				<PunchupResponsesTable
					responsesQueryObject={responsesQueryObject}
					isAttendeesList={isAttendeesList}
					eventMode={eventMode}
					sortBy={sortBy}
					order={order}
					onSort={handleSort}
					visibleStaticKeys={effectiveVisibleKeys}
					questions={questions}
					visibleCustomKeys={visibleCustomKeys}
					renderRowActions={renderRowActions}
					isRefetching={isBackgroundRefetch(responsesQueryObject)}
				/>
			)}
		</PunchupTablePage>
	);
};
